// Confirm a bulk dismissal.
//
// Dismissal takes no reason and hides the lines behind a toggle, which is the
// fastest way to clear a backlog and also the easiest to over-apply. This dialog
// is the one guard that survives having no reason field: it names the count and
// the total before committing, so a bulk action always shows its scope.
//
// Deliberately NOT the full destructive-confirm treatment of WriteOffModal.
// Dismissal moves no money and is undone in one click from the Show dismissed
// view, so dressing it up as irreversible would be dishonest and would slow down
// the one action that needs to stay fast.

import React from "react";
import { Alert, Button, Modal, Spinner, Table } from "react-bootstrap";
import { zar } from "../Accounts/lineAllocation";

export default function EraDismissModal({
  show,
  onHide,
  lines,
  total,
  submitting,
  onConfirm,
}) {
  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header
        closeButton={!submitting}
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Dismiss {lines.length} remittance line{lines.length === 1 ? "" : "s"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="warning" className="mb-3">
          This takes <strong>{zar.format(total)}</strong> of remittance off your
          outstanding list. It does not pay anything and does not change the
          invoices: the amounts stay outstanding on the account.
        </Alert>

        <p className="small text-muted">
          Dismissed lines are hidden behind the Show dismissed toggle and can be
          restored from there at any time.
        </p>

        <div className="d-none d-lg-block">
          <Table bordered hover responsive size="sm">
            <thead className="text-center">
              <tr>
                <th className="text-center align-middle text-primary">Patient</th>
                <th className="text-center align-middle text-primary">Tariff</th>
                <th className="text-center align-middle text-primary acc-list-num">
                  Paid
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={`${l.eraLogId}:${l.schemeClaimId}:${l.eraLineKey}`}>
                  <td>{l.patientName || "-"}</td>
                  <td>{l.tariffCode || "-"}</td>
                  <td className="acc-list-num">{zar.format(l.paidAmount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="fw-semibold">
                <td colSpan={2}>Total</td>
                <td className="acc-list-num">{zar.format(total)}</td>
              </tr>
            </tfoot>
          </Table>
        </div>

        <div className="d-lg-none">
          {lines.map((l) => (
            <div
              className="acc-card"
              key={`${l.eraLogId}:${l.schemeClaimId}:${l.eraLineKey}`}
            >
              <div className="acc-card-top">
                <span className="acc-card-id">{l.tariffCode || "Line"}</span>
                <span className="acc-card-amount">{zar.format(l.paidAmount)}</span>
              </div>
              <div className="acc-card-body">
                <div className="acc-card-field">
                  <span className="acc-card-label">Patient</span>
                  <span className="acc-card-value">{l.patientName || "-"}</span>
                </div>
              </div>
            </div>
          ))}
          <p className="fw-semibold text-end mt-2">Total {zar.format(total)}</p>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-danger"
          onClick={onConfirm}
          disabled={submitting}
          style={{ minWidth: 140 }}
        >
          {submitting ? (
            <Spinner animation="border" size="sm" role="status" />
          ) : (
            <>
              <i className="fa-solid fa-eye-slash me-2" />
              Dismiss
            </>
          )}
        </Button>
        <Button
          variant="outline-secondary"
          onClick={onHide}
          disabled={submitting}
          style={{ minWidth: 140 }}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
